/**
 * Roomgrid topbar (56px).
 *
 * Contents, left to right:
 *  - Sidebar toggle (react-admin's, restyled).
 *  - Page title (react-admin TitlePortal — each page sets it via <Title>).
 *  - Page actions slot (pages project CTAs here via <TopbarActions>).
 *  - Locale switch, theme toggle, loading indicator.
 *
 * The user menu is NOT here anymore — it lives in the sidebar footer
 * (SidebarUserChip).
 */

import React from "react";
import {
  AppBar,
  TitlePortal,
  LocalesMenuButton,
  ToggleThemeButton,
  LoadingIndicator,
} from "react-admin";
import { Box, useTheme } from "@mui/material";
import { TopbarActionsTarget } from "./TopbarActionsContext";

const TOPBAR_HEIGHT = 56;

export const CustomAppBar: React.FC = () => {
  const theme = useTheme();
  const rg = theme.custom.rg;

  return (
    <AppBar
      userMenu={false}
      elevation={0}
      position="static"
      toolbar={
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <LocalesMenuButton />
          <ToggleThemeButton />
          <LoadingIndicator />
        </Box>
      }
      sx={{
        backgroundColor: rg.bgElev,
        color: rg.text,
        borderBottom: `1px solid ${rg.border}`,
        boxShadow: "none",

        "& .MuiToolbar-root": {
          height: TOPBAR_HEIGHT,
          minHeight: `${TOPBAR_HEIGHT}px !important`,
          padding: "0 20px 0 12px",
          gap: "10px",
          backgroundColor: "transparent",
        },

        // Sidebar toggle + toolbar icon buttons: 36px quiet squares.
        "& .MuiToolbar-root .MuiIconButton-root": {
          width: 36,
          height: 36,
          borderRadius: "8px",
          border: "1px solid transparent",
          color: rg.text2,
          "&:hover": {
            backgroundColor: rg.bg2,
            borderColor: "transparent",
          },
          "& .MuiSvgIcon-root": { fontSize: 19 },
        },

        // Locale switch button (text button showing the language). Scoped to
        // text-variant buttons only so projected page CTAs (contained,
        // near-black with light text) keep their own colors.
        "& .MuiToolbar-root .MuiButton-text": {
          minHeight: 30,
          color: rg.text2,
        },
      }}
    >
      {/* Page title — 19 / 700 / -0.02em per handoff. */}
      <TitlePortal
        variant="h5"
        sx={{
          fontSize: "19px",
          fontWeight: 700,
          letterSpacing: "-0.02em",
          color: rg.text,
          flex: 1,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      />

      {/* Page actions slot (filled by <TopbarActions> from pages). */}
      <TopbarActionsTarget />
    </AppBar>
  );
};

export default CustomAppBar;
